import forms from "../components/ui/field.module.css";
import { FormEvent, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { useAuth } from "../lib/auth";
import styles from "./login.module.css";

interface LoginLocationState {
  from?: { pathname?: string; search?: string };
}

function authErrorMessage(err: unknown) {
  const code =
    typeof err === "object" && err && "code" in err ? String(err.code) : "";
  if (
    code === "auth/invalid-credential" ||
    code === "auth/wrong-password" ||
    code === "auth/user-not-found"
  ) {
    return "Email or password is incorrect.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Try again later.";
  }
  return "Login failed. Try again.";
}

export default function LoginPage() {
  const { configured, loading, signIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LoginLocationState | null;
  const redirectTo = `${state?.from?.pathname || "/events"}${state?.from?.search || ""}`;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, navigate, redirectTo, user]);

  if (!loading && user) {
    return <Navigate to={redirectTo} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={styles.page}>
      <section className={styles.panel}>
        <div className={styles.heading}>
          <p className={styles.eyebrow}>Rental Admin</p>
          <h1 className={styles.title}>Login</h1>
        </div>

        {!configured ? (
          <Banner variant="error">
            Firebase Auth is not configured for this build.
          </Banner>
        ) : (
          <form onSubmit={onSubmit} className={styles.form}>
            <label className={styles.field}>
              <span className={forms.label}>Email</span>
              <input
                autoComplete="email"
                className={forms.input}
                disabled={submitting}
                inputMode="email"
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </label>

            <label className={styles.field}>
              <span className={forms.label}>Password</span>
              <input
                autoComplete="current-password"
                className={forms.input}
                disabled={submitting}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
            </label>

            {error ? <Banner variant="error">{error}</Banner> : null}

            <div className={styles.submitRow}>
              <Button
                className={styles.submit}
                disabled={submitting}
                loading={submitting}
                type="submit"
              >
                Login
              </Button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
